// src/components/resources/calculators/CPVCalculator.logic.ts
//
// Pure calculation logic for the CPV (cost per view) calculator. Split into a
// sibling module (no `'use client'`) so the math — CPV, view-rate clamping,
// CPM, and cost-per-ThruPlay — is unit-testable in isolation, mirroring the
// `ROASCalculator.logic` / `MarketingBudgetCalculator.logic` split. The
// component imports these types + `calculateMetrics`.

export interface CalculatorInputs {
  adSpend: number
  views: number
  impressions?: number
  thruplays?: number
}

export interface CalculatorResults {
  /** The inputs this result was computed from, captured at calculate time so a
   *  later edit to the live inputs can't desync the emailed/CRM-stored payload
   *  from the visible result card. */
  inputs: CalculatorInputs
  cpv: number
  viewRate?: number
  /** True when raw views > impressions, so view rate was clamped to 100%
   *  (views are a subset of impressions; a >100% rate is physically
   *  impossible and signals a data-entry slip). */
  viewRateClamped?: boolean
  cpm?: number
  costPerThruplay?: number
  /** True when ThruPlays > views. For same-campaign Meta data a ThruPlay is a
   *  stricter/subset view definition, so it can't exceed total views; flag it
   *  so the UI can warn rather than imply a cost-per-ThruPlay below the CPV. */
  thruplaysExceedViews?: boolean
}

/**
 * Derive CPV plus the optional view-rate / CPM / cost-per-ThruPlay metrics.
 * Callers gate on `adSpend > 0 && views > 0` (a zeroed required input makes CPV
 * a divide-by-zero); the optional metrics are only produced when their own
 * input is positive.
 */
export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  const results: CalculatorResults = {
    // Snapshot the inputs so downstream consumers (email/CRM payload) read the
    // values that produced this result, not whatever the live form holds later.
    inputs: { ...inputs },
    cpv: inputs.adSpend / inputs.views
  }

  if (inputs.impressions && inputs.impressions > 0) {
    const rawViewRate = (inputs.views / inputs.impressions) * 100
    // Views are a subset of impressions, so view rate can't exceed 100%.
    // Clamp the display value and flag it so the UI can hint at the bad input
    // rather than rendering an impossible "200%".
    results.viewRate = Math.min(rawViewRate, 100)
    results.viewRateClamped = rawViewRate > 100
    results.cpm = (inputs.adSpend / inputs.impressions) * 1000
  }

  if (inputs.thruplays && inputs.thruplays > 0) {
    results.costPerThruplay = inputs.adSpend / inputs.thruplays
    // ThruPlays are a stricter, subset view definition — more ThruPlays than
    // total views is impossible for same-campaign data and would render a
    // cost-per-ThruPlay below the headline CPV. Flag it for an inline warning.
    results.thruplaysExceedViews = inputs.thruplays > inputs.views
  }

  return results
}
